import { Card, Stack, Typography } from '@mui/material';
import dayjs from 'dayjs';
import useUnitProvider from '/@/core/context/unit';
import { convertAreaFromM2 } from '/@/hooks/geometry/area';
import { getClimatePatternChoices } from '/@/hooks/recommendation';
import { _ } from '/@/locale';
import type { Field } from '/@/types/field';

/** One of the four figures across the top of the result page. */
function DataCard({ label, value, detail }: { label: string; value: string; detail?: string }) {
    return (
        <Card elevation={0} variant='outlined' sx={{ borderRadius: '4px', p: 2, height: '100%' }}>
            <Stack spacing={0.5}>
                <Typography variant='subtitle1' color='primary'>
                    {label}
                </Typography>
                <Typography variant='h4' fontWeight={700} color='primary'>
                    {value}
                </Typography>
                {detail && (
                    <Typography variant='subtitle1' color='primary.mui.inactive'>
                        {detail}
                    </Typography>
                )}
            </Stack>
        </Card>
    );
}

const NOT_AVAILABLE = '-';

/** Grower, field, planting date and environment — the inputs the recommendation was built on. */
function GeneralData({
    grower,
    field,
    plantingDate,
    environment,
    climatePattern,
    soilMoisture,
    crop = 'corn',
}: {
    grower?: { name?: string; email?: string };
    field?: Field;
    plantingDate?: string;
    environment?: number;
    climatePattern?: number;
    soilMoisture?: number;
    crop?: 'corn' | 'soy';
}) {
    const units = useUnitProvider();

    const area = field?.area ? convertAreaFromM2(field.area, units).toString() : undefined;
    const yieldValue = environment !== undefined ? units.toYield(environment, crop, 0) : undefined;
    const climatePatternLabel = getClimatePatternChoices().find(
        (choice: { value: number; label: string }) => choice.value === climatePattern
    )?.label;
    const environmentDetail = [
        climatePatternLabel,
        soilMoisture !== undefined ? _('{{value}}% humedad', { value: soilMoisture }) : undefined,
    ]
        .filter(Boolean)
        .join(' - ');

    return (
        <Stack
            display='grid'
            gap={2}
            gridTemplateColumns={{ xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(4, 1fr)' }}
            alignItems='stretch'
        >
            <DataCard label={_('Productor')} value={grower?.name || NOT_AVAILABLE} detail={grower?.email} />
            <DataCard
                label={_('Lote')}
                value={field?.name || NOT_AVAILABLE}
                detail={[area, field?.location].filter(Boolean).join(' - ') || undefined}
            />
            <DataCard
                label={_('Fecha de siembra')}
                value={plantingDate ? dayjs(plantingDate).format('DD/MM/YYYY') : NOT_AVAILABLE}
            />
            <DataCard
                label={_('Ambiente')}
                value={yieldValue ? `${yieldValue.localeValue} ${yieldValue.unit}` : NOT_AVAILABLE}
                detail={environmentDetail || undefined}
            />
        </Stack>
    );
}

export default GeneralData;
